function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Sparkline } from "../../components/analytics-kit";
import PlunkDropDown from "../../components/plunk-dropdown";
import publishSearch from "../../functions/publish-search";
import publishEvent from "../../functions/publish-event";
import { fetchForecast, fetchV1, num, usdShort, shortDate, outlookOf, ForecastsHeader, fetchDesignCount, Wait } from "./forecast-common";
import { decodeEntities } from "../stores/common";

/*
 * Forecasts · Your products (board "4 · Forecasts", approved 2026-10-09).
 *
 * Two independent loads, rendered as they land:
 *   1. /forecast/product-forecast/overview   every product forecast in one call (title, image,
 *      category, expected website views, monthly interest curve). The list shows at once.
 *   2. /sales/explorer/products/year         last 12 months of sales + the 12-month sales forecast
 *      per product (the same model as the product page's "What's coming"). Joined by product id;
 *      fills the money columns, the tiles and the Growing / Slowing outlook when it arrives.
 * The old per-row Refresh link is gone: forecasts are refreshed by the platform, not per row.
 */

const PAGE = 15;
const OUTLOOK_COLOR = "#14b8a6";
class MainComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "open", r => {
      this.props.history.push({
        pathname: `/Forecast/Product/Detail/${r.id}`,
        state: {
          row: r
        }
      });
    });
    this.state = {
      rows: null,
      error: false,
      updated: null,
      oldest: null,
      sales: null,
      salesError: false,
      designs: null,
      filter: "all",
      category: "all",
      q: "",
      showAll: false
    };
    this._alive = true;
  }
  componentDidMount() {
    publishSearch("Forecasts");
    publishEvent("reset-search", {});
    this.loadForecasts();
    this.loadSales();
    fetchDesignCount().then(n => {
      if (this._alive) this.setState({
        designs: n
      });
    });
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async loadForecasts() {
    try {
      const d = await fetchForecast("/forecast/product-forecast/overview");
      if (!this._alive) return;
      this.setState({
        rows: d && d.items || [],
        updated: d && d.updated,
        oldest: d && d.oldest
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true,
        rows: []
      });
    }
  }

  // Explorer pages of 500; the first call tells us the total, the rest go in parallel.
  async loadSales() {
    try {
      const url = skip => `/sales/explorer/products/year?top=500&skip=${skip}&sort=forecast&dir=desc`;
      const first = await fetchV1(url(0));
      let rows = first && first.rows || [];
      const total = Number(first && first.total) || rows.length;
      const skips = [];
      for (let s = 500; s < total && s < 10000; s += 500) skips.push(s);
      const more = await Promise.all(skips.map(s => fetchV1(url(s)).then(r => r && r.rows || []).catch(() => [])));
      more.forEach(m => {
        rows = rows.concat(m);
      });
      const map = {};
      rows.forEach(r => {
        const k = String(r.productId || r.sku || "").toLowerCase();
        if (k) map[k] = r;
      });
      if (this._alive) this.setState({
        sales: map
      });
    } catch (e) {
      if (this._alive) this.setState({
        salesError: true,
        sales: {}
      });
    }
  }

  // Forecast rows joined with their sales numbers.
  joined() {
    const {
      rows,
      sales
    } = this.state;
    if (!rows) return null;
    return rows.map(r => {
      const s = sales ? sales[String(r.productId || "").toLowerCase()] : null;
      const last = s ? Number(s.revenue) || 0 : 0;
      const next = s ? Number(s.forecastRevenue12) || 0 : 0;
      return {
        ...r,
        id: String(r.id),
        sku: s && s.sku || r.productId,
        // feeds can send categories HTML-escaped ("Furniture &gt; Sofas")
        category: decodeEntities(s && s.category || r.category) || null,
        last,
        next,
        low: s ? Number(s.forecastLow12) || 0 : 0,
        high: s ? Number(s.forecastHigh12) || 0 : 0,
        outlook: sales ? outlookOf(last, next) : null,
        // real product page views, trailing 12 months (product_stats); stored forecast as fallback
        viewsActual: r.actualStart ? Number(r.actualViews) || 0 : Number(r.views) || 0,
        // shape: the expected curve when the last forecast run has one, else the actual months
        shape: (r.interestViews || []).some(v => Number(v) > 0) ? r.interestViews : r.actualMonthly || []
      };
    });
  }
  renderTiles(all) {
    const {
      sales,
      updated,
      oldest
    } = this.state;
    const loading = !sales;
    let total = 0,
      varSum = 0,
      growing = 0,
      slowing = 0;
    (all || []).forEach(r => {
      total += r.next;
      // Combine per-product ranges as independent errors (root of summed squares), so the
      // likely range for the whole list is not the sum of every product's worst case.
      const half = Math.max(0, (r.high - r.low) / 2);
      varSum += half * half;
      if (r.outlook && r.outlook.key === "up") growing += 1;
      if (r.outlook && r.outlook.key === "down") slowing += 1;
    });
    const spread = Math.sqrt(varSum);
    const wait = /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-wait"
    });
    const n = all ? all.length : 0;
    const sameDay = updated && oldest && String(updated).slice(0, 10) === String(oldest).slice(0, 10);
    return /*#__PURE__*/React.createElement("div", {
      className: "fc-tiles"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-tile"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-l"
    }, "Next 12 months"), /*#__PURE__*/React.createElement("b", {
      className: "fc-tile-v"
    }, loading ? wait : usdShort(total)), /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-s"
    }, loading ? "adding up the sales forecast" : total > 0 ? `likely range ${usdShort(Math.max(0, total - spread))} to ${usdShort(total + spread)}` : "no sales forecast yet")), /*#__PURE__*/React.createElement("div", {
      className: "fc-tile"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-l"
    }, "Growing"), /*#__PURE__*/React.createElement("b", {
      className: "fc-tile-v"
    }, loading ? wait : num(growing)), /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-s"
    }, "products expected to sell more than last year")), /*#__PURE__*/React.createElement("div", {
      className: "fc-tile"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-l"
    }, "Slowing"), /*#__PURE__*/React.createElement("b", {
      className: "fc-tile-v"
    }, loading ? wait : num(slowing)), /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-s"
    }, "expected to sell less")), /*#__PURE__*/React.createElement("div", {
      className: "fc-tile"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-l"
    }, "Updated"), /*#__PURE__*/React.createElement("b", {
      className: "fc-tile-v"
    }, all ? updated ? shortDate(updated) : "Not yet" : wait), /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-s"
    }, all ? `latest forecast run · ${num(n)} products${oldest && !sameDay ? `, oldest from ${shortDate(oldest)}` : ""} · sales and views are current` : "")));
  }
  renderList(all) {
    const {
      sales,
      filter,
      category,
      q,
      showAll,
      error
    } = this.state;
    if (!all) return /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement(Wait, null), /*#__PURE__*/React.createElement(Wait, null));
    const cats = Array.from(new Set(all.map(r => r.category).filter(Boolean))).sort((a, b) => a.localeCompare(b));
    const needle = q.trim().toLowerCase();
    const scoped = all.filter(r => (category === "all" || r.category === category) && (!needle || String(r.title || "").toLowerCase().indexOf(needle) > -1 || String(r.sku || "").toLowerCase().indexOf(needle) > -1));
    const count = k => scoped.filter(r => r.outlook && r.outlook.key === k).length;
    const rows = scoped.filter(r => filter === "all" || r.outlook && r.outlook.key === filter).sort((a, b) => sales ? b.next - a.next || b.viewsActual - a.viewsActual : b.viewsActual - a.viewsActual);
    const shown = showAll ? rows : rows.slice(0, PAGE);
    return /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Your products"), /*#__PURE__*/React.createElement("span", {
      className: "fc-card-hint"
    }, sales ? "sorted by next 12 months" : "sorted by website views while sales load"), /*#__PURE__*/React.createElement("span", {
      className: "fc-grow"
    }), /*#__PURE__*/React.createElement("span", {
      className: "fc-chips"
    }, [["all", "All", scoped.length], ["up", "Growing", count("up")], ["down", "Slowing", count("down")]].map(([k, l, n]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      className: `fc-chip${filter === k ? " on" : ""}`,
      onClick: () => this.setState({
        filter: k,
        showAll: false
      }),
      disabled: !sales && k !== "all"
    }, l, sales ? /*#__PURE__*/React.createElement("span", {
      className: "fc-chip-n"
    }, num(n)) : null))), cats.length > 1 ? /*#__PURE__*/React.createElement("span", {
      className: "fc-dd"
    }, /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "180px",
      value: category,
      onChange: v => this.setState({
        category: v,
        showAll: false
      }),
      options: [["all", "All categories"], ...cats.map(c => [c, c])]
    })) : null, /*#__PURE__*/React.createElement("input", {
      className: "fc-search",
      type: "search",
      value: q,
      placeholder: "Search products",
      "aria-label": "Search products",
      onChange: e => this.setState({
        q: e.target.value,
        showAll: false
      })
    })), error ? /*#__PURE__*/React.createElement("div", {
      className: "fc-empty"
    }, "Forecasts could not be loaded. Try again in a minute.") : null, /*#__PURE__*/React.createElement("div", {
      className: "fc-list",
      role: "table"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-row head",
      role: "row"
    }, /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null, "Product"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Last 12 months"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Next 12 months"), /*#__PURE__*/React.createElement("span", {
      className: "n",
      title: "Product page views over the last 12 months"
    }, "Website views"), /*#__PURE__*/React.createElement("span", {
      title: "Views by month: the expected curve when the forecast has one, otherwise the last 12 months"
    }, "Shape of the year"), /*#__PURE__*/React.createElement("span", null, "Outlook")), shown.map(r => /*#__PURE__*/React.createElement("div", {
      key: r.id,
      className: "fc-row click",
      role: "row",
      onClick: () => this.open(r),
      title: `Open the forecast for ${r.title}`
    }, /*#__PURE__*/React.createElement("span", null, r.imageLink ? /*#__PURE__*/React.createElement("img", {
      className: "fc-thumb",
      src: r.imageLink,
      alt: "",
      loading: "lazy",
      onError: e => {
        e.currentTarget.style.visibility = "hidden";
      }
    }) : /*#__PURE__*/React.createElement("span", {
      className: "fc-thumb"
    })), /*#__PURE__*/React.createElement("span", {
      className: "fc-prod"
    }, /*#__PURE__*/React.createElement("b", null, r.title), /*#__PURE__*/React.createElement("span", null, [r.sku, r.category].filter(Boolean).join(" · "))), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, sales ? r.last > 0 ? usdShort(r.last) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "none") : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "\u2026")), /*#__PURE__*/React.createElement("span", {
      className: "n strong"
    }, sales ? r.next > 0 ? usdShort(r.next) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "none") : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "\u2026")), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, num(r.viewsActual)), /*#__PURE__*/React.createElement("span", {
      className: "fc-spark"
    }, r.shape.length > 1 && r.shape.some(v => Number(v) > 0) ? /*#__PURE__*/React.createElement(Sparkline, {
      points: r.shape,
      height: 26,
      color: OUTLOOK_COLOR
    }) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "no visits yet")), /*#__PURE__*/React.createElement("span", null, r.outlook ? /*#__PURE__*/React.createElement("span", {
      className: `fc-pill ${r.outlook.tone}`
    }, r.outlook.label) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "\u2026")))), !shown.length && !error ? /*#__PURE__*/React.createElement("div", {
      className: "fc-empty"
    }, all.length ? "No products match. Try another search or filter." : "No product forecasts yet.") : null), /*#__PURE__*/React.createElement("div", {
      className: "fc-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-foot-note"
    }, "Click a product for its full forecast. Sales are the last 12 months and the next 12 expected; website views are product page views over the last 12 months.", this.state.salesError ? " Sales numbers could not be loaded right now." : ""), rows.length > PAGE ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "fc-link",
      onClick: () => this.setState({
        showAll: !showAll
      })
    }, showAll ? "Show fewer" : `Show all ${num(rows.length)}`) : null));
  }
  render() {
    const all = this.joined();
    return /*#__PURE__*/React.createElement("div", {
      className: "fc-view territory-view wa-view"
    }, /*#__PURE__*/React.createElement(ForecastsHeader, {
      active: "products",
      designs: this.state.designs
    }), this.renderTiles(all), this.renderList(all));
  }
}
const RoutableComponent = withRouter(MainComponent);
export class ForecastProductViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(RoutableComponent, {
      params: this.props.params
    });
  }
}
export default {
  ForecastProductViewComponent
};